import React from 'react'
import { Trans } from 'react-i18next'
import moment from 'moment'
import t from '../../../translations/main'
import globalConfig from './config'
import globalConsts from 'src/views/Forms/Onboarding/globalConsts'

import Icon from '../../Core/Iconography/Icon/Icon'
import Label from '../../Atoms/Label/Label'
import Grid from '../../Layout/Grid/Grid'
import Input from '../../Atoms/Input/Input'
import Dropdown from '../../Atoms/Dropdown/Dropdown'
import RadioGroup from '../../Molecules/RadioGroup/RadioGroup'
import DatePicker from '../../Molecules/DatePicker/DatePicker'
import InputMultipleNumbers from '../../Atoms/InputMultipleNumbers/InputMultipleNumbers'
import Accordion from '../Accordion/Accordion'
import RadioButton from '../../Atoms/RadioButton/RadioButton'
import Checkbox from '../../Atoms/Checkbox/Checkbox'

import { ActionIconWrapper } from './Form.styles'
import { InitialState, InitialStateValue } from '../../Core/Hooks/useForm/useForm'
import {
  ApiData,
  TableConfigParam,
  TableObj,
} from '../../../views/Pages/Onboarding/types'
import {
  ComponentType,
  ComponentDataType,
  SectionDataType,
  ConfigDataType,
  KeysToValidate,
  RegexType,
  CheckModeInputs
} from './types'
import { RequestConfig } from '../../Core/Hooks/useFetch/types'
import { DropdownOptionsContextProps } from 'src/components/Core/Context/OnboardingContext'
import timezones from 'src/views/Forms/Onboarding/CorporateAccount/BankDetails/timezones'
import storage from 'src/components/Core/Utils/storage'
import { OptionsListProps } from 'src/components/Molecules/StepProgressBar/types'

const listMappings: Record<string, string[]> = {
  title: ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Other'],
  employmentStatus: ['Employed', 'Not working at present', 'Self-employed', 'Retired'],
  maritalStatus: ['Divorced', 'Married', 'Single', 'Widowed'],
  grossAnnualIncomeType: ['Less than £25,000','£25,000 to £49,999','£50,000 to £99,999','£100,000 to £199,999','£200,000 to £299,999','over £300,000'],
  netAssestsType: ['Less than £25,000','£25,000 to £49,999','£50,000 to £99,999','£100,000 to £199,999','£200,000 to £299,999','£300,000 to £499,999', '£500,000 to £749,999', '£750,000 to £999,999', 'over £1,000,000'],
  riskProfileCategory: ['I want to hedge','I want to keep my capital safe and avoid any risk','I accept a calculable risk in order to achieve a profit (maximum potential downside is calculable before entering a trade)','I have a high profit expectation and am willing to accept a high level of risk',]
}

const phoneValidationMap = {
  validationRegisteredTelephoneCountryIndex: {
    countryCode: 'registeredTelephoneCountryIndex',
    phoneNumber: 'registeredTelephoneNo'
  },
  validationTelephoneCountryIndex: {
    countryCode: 'telephoneCountryIndex',
    phoneNumber: 'tradingTelephoneNo'
  },
  validationManagementTelephoneCountryIndex: {
    countryCode: 'managementTelephoneCountryIndex',
    phoneNumber: 'managementContactPhoneNo'
  },
  validationTradingContactTelephoneCountryIndex: {
    countryCode: 'tradingContactTelephoneCountryIndex',
    phoneNumber: 'tradingPhoneNo'
  },
  validationBackOfficeTelephoneCountryIndex: {
    countryCode: 'backOfficeTelephoneCountryIndex',
    phoneNumber: 'backOfficePhoneNo'
  },
  validationComplianceTelephoneCountryIndex: {
    countryCode: 'complianceTelephoneCountryIndex',
    phoneNumber: 'compliancePhoneNo'
  },
  validationCertifiedTelephCountryIndex: {
    countryCode: 'certifiedTelephCountryIndex',
    phoneNumber: 'telephone'
  },
  validationTelephoneCountryIndexUk: {
    countryCode: 'contactPhoneNumberCountryIndex',
    phoneNumber: 'contactPhoneNumber'
  },
  validationDayTimeTelephoneCountryIndex: {
    countryCode: 'dayTimeTelephoneCountryIndex',
    phoneNumber: 'dayTimeTelephone'
  },
  validationEveningTelephoneCountryIndex: {
    countryCode: 'eveningTelephoneCountryIndex',
    phoneNumber: 'eveningTelephone'
  },
  validationFaxNumberCountryIndex: {
    countryCode: 'faxNumberCountryIndex',
    phoneNumber: 'faxNumber'
  },
  validationMobileNumberCountryIndex: {
    countryCode: 'mobileNumberCountryIndex',
    phoneNumber: 'mobileNumber'
  },
  validationPhoneNumberCountryIndex: {
    countryCode: 'phoneNumberCountryIndex',
    phoneNumber: 'phoneNumber'
  }
}

const Components: ComponentType = {
  input: Input,
  dropdown: Dropdown,
  radioGroup: RadioGroup,
  date: DatePicker,
  label: Label,
  inputMultipleNumbers: InputMultipleNumbers,
  accordion: Accordion,
  radioButton: RadioButton,
  checkbox: Checkbox,
}
// eslint-disable-next-line
const getSpecificProps = (component: string, formData: InitialState, props: any) => {
  const specificMap: { [key: string]: Record<string, unknown> } = {
    input: {
      maxlength: props.maxlength || '255'
    },
    checkbox: {
      checked: isChecked(formData, props),
    },
    radioButton: {
      defaultChecked: formData?.[props?.name as string]?.value === props.value,
      value: props.value
    },
    dropdown: {
      outsideIndexSelected: props.value
    }
  }
  return specificMap[`${component}`]
}

export const checkMode = (mode?: CheckModeInputs, optionsList?: OptionsListProps[], currentStep?: number) => {
  const prospectPermission = storage.get(storage?.keys?.onboarding?.prospectPermission)
  const applicationStatus = storage.get(storage?.keys?.onboarding?.applicationStatus)
  const applicationResubmitted = storage.get(storage?.keys?.onboarding?.applicationResubmitted)

  const isDocOrSubmitted = prospectPermission === 'Documentation'
  || (['submitted', 'aof_approved', 'generating_signature_pack', 'signature_received', 'signature_rejected', 'aof_completed'].includes(applicationStatus))
  || (applicationStatus === 'awaiting_signature' && (currentStep || (currentStep === 0)) && currentStep !== optionsList?.length - 1)

  const modeMap: Record<CheckModeInputs, boolean> = {
    readOnly: isDocOrSubmitted,
    stepsReadOnly: applicationResubmitted
            && applicationStatus === 'in_progress'
            && optionsList?.[`${currentStep}`].state === 'complete',
    enableComments: isDocOrSubmitted || applicationResubmitted,
    enableCommentsOnSignature: ['awaiting_signature', 'signature_rejected', 'signature_received'].includes(applicationStatus)
  }

  return modeMap[`${mode}`] || false

  // the applications that documentation team has access is calculated on the backend, so if they have access is because the application was already submitted at some point
  // if prospectPermission is 'Documentation' -> read only
  // for the documentation team the only change is if the approve/revert buttons appears, if applicationStatus is in_progress then the buttons do NOT appear
  // if prospectPermission is different than 'Documentation' -> check if applicationStatus is submitted -> if true -> read only
  // if prospectPermission is different than 'Documentation' -> check if applicationStatus is in_progress -> if true -> check if applicationResubmitted -> if true -> write only in steps with warning status
}

export const injectTranslation = (config: ConfigDataType): ConfigDataType => {
  const { path } = config
  const output = { ...config }
  if (config.needTranslationInjection === false) return output
  config.content.forEach((section: SectionDataType, sectionIndex: number) =>
    section.content.forEach(
      (component: ComponentDataType, componentIndex: number) => {
        if (component?.componentType === 'grid')
          return injectTranslation(component)

        if (component.componentType === 'radioButton') return

        if (!component?.props?.skipLabel && t(`${path}.${component?.props?.name}.label`) !== '' && !t(`${path}.${component?.props?.name}.label`).includes('undefined')) {
          output.content[`${sectionIndex}`].content[`${componentIndex}`].props.label =
            // @ts-ignore:next-line
            <Trans i18nKey={`${path}.${component?.props?.name}.label`} components={component?.props?.translationComponent || null} values={{...component?.props?.translationValues}} /> || ''
        }

        if (t(`${path}.${component?.props?.name}.placeholder`) !== '' && !t(`${path}.${component?.props?.name}.placeholder`).includes('undefined')) {
          output.content[`${sectionIndex}`].content[`${componentIndex}`].props.placeholder =
            t(`${path}.${component?.props?.name}.placeholder`) || ''
        }

        if (component.componentType === 'dropdown') {
          if(component.props?.isManageTeamRoleDropdown) {
            const options = component.props.isClient
              ? t(`${path}.${component.props.name}.optionsClient`, {returnObjects: true})
              : t(`${path}.${component.props.name}.optionsProspect`, {returnObjects: true})
            output.content[`${sectionIndex}`].content[`${componentIndex}`].props.options = options
          } else {
            output.content[`${sectionIndex}`].content[`${componentIndex}`].props.options = t(`${path}.${component.props.name}.options`, {
              returnObjects: true,
            }) || []
          }
        }

        if (t(`${path}.${component?.props?.name}.tooltipLabel`) !== '' && !t(`${path}.${component?.props?.name}.tooltipLabel`).includes('undefined')) {
          output.content[`${sectionIndex}`].content[`${componentIndex}`].props.tooltipLabel = t(
            `${path}.${component?.props?.name}.tooltipLabel`
          ) === `${path}.${component?.props?.name}.tooltipLabel` ? '' :  t(
              `${path}.${component?.props?.name}.tooltipLabel`
            )
        }

      }
    )
  )
  return output
}

const componentsBuilder = (
  index: number,
  componentData: ComponentDataType,
  formData: InitialState,
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
) => {
  const { componentType, props } = componentData
  if (
    typeof formData?.[props?.name as string]?.show === 'boolean' &&
    !formData?.[props?.name as string]?.show
  )
    return null

  if (componentData.visualName && formData[componentData?.visualName]?.value !== 'true') return null

  // if the component is custom and there is no name associated with it then it should be SHOW all the time
  if (componentData?.custom && !componentData.visualName) {
    if (!componentData.secondaryName || componentData.secondaryName === formData[props.name].secondaryName) {
      return (
        <componentData.custom
          key={index}
          customOnChange={handleInputChange}
          value={formData?.[props?.name as string]?.value || ''}
          numberOfFiles={formData?.numberOfFiles?.value ? parseInt(formData?.numberOfFiles?.value) : 0}
          disabled={!props?.neverDisabled && checkMode('readOnly')}
        />
      )
    }
  }

  // if the component is visual and there is no name associated with it then it should be SHOW all the time
  if (componentData?.visualComponent && !componentData.visualName) {
    return componentData.visualComponent
  }

  // if the component exists and there IS a name associated with it then its SHOW/HIDE should obey the VALUE property, checking also if it is custom or not
  if (formData[componentData?.visualName]?.value === 'true') {
    if (!componentData.custom) {
      return componentData.visualComponent
    } else if (componentData.custom) {
      return (
        <componentData.custom
          key={index}
          customOnChange={handleInputChange}
          value={formData?.[props?.name as string]?.value || ''}
          numberOfFiles={formData?.numberOfFiles?.value ? parseInt(formData?.numberOfFiles?.value) : 0}
          disabled={!props?.neverDisabled && checkMode('readOnly')}
        />
      )
    }
  }

  // if the component needs a secondaryName due repetion in hidden sections then it should obey the matching between the formData secondaryName with self secondaryName
  if (componentData?.secondaryName && componentData.secondaryName !== formData[props.name].secondaryName) return null

  const specific = getSpecificProps(componentType, formData, props) || {}

  if (typeof Components[componentType as string] !== 'undefined') {
    const Component = Components[componentType as string]
    return (
      <Component
        key={index}
        {...props}
        value={formData?.[props?.name as string]?.value || ''}
        {...specific}
        onChange={handleInputChange}
        readOnly={!props?.neverDisabled && checkMode('readOnly')}
        disabled={!props?.neverDisabled &&
          (
            checkMode('readOnly') ||
            (
              props.isTextarea &&
              storage.get(storage?.keys?.onboarding?.applicationResubmitted) &&
              (storage.get(storage?.keys?.onboarding?.applicationStatus) === 'in_progress' || storage.get(storage?.keys?.onboarding?.applicationStatus) === 'awaiting_signature') &&
              storage.get(storage?.keys?.onboarding?.stepCompleted) === 'complete'
            )
          )
        }
      />
    )
  }
  console.error(`The component ${componentType} is undefined`)
  return null
}

const sectionBuilder = (
  config: ConfigDataType,
  formData: InitialState,
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
) => {
  return config?.content.map((obj: SectionDataType, index: number) => {
    return (
      <Grid key={index} {...obj.props}>
        {obj.content.map(
          (componentObj: ComponentDataType, componentIndex: number) => {
            if (componentObj?.componentType === 'grid')
              return sectionBuilder(componentObj, formData, handleInputChange)
            return componentsBuilder(
              componentIndex,
              componentObj,
              formData,
              handleInputChange
            )
          }
        )}
      </Grid>
    )
  })
}

export const transformStringToNumOrViceVersa = (
  value: string | number,
  isGet: boolean
) => {
  if (isGet) return typeof value === 'number' ? value.toString() : ''

  return typeof value === 'string' ? Number(value) : 0
}

export const booleanToString = (value: string | boolean): string => {
  return typeof value === 'boolean' ? (value ? 'true' : 'false') : value
}

export const stringToBoolean = (value: string | boolean): boolean => {
  return typeof value === 'string' ? value === 'true' : value
}

export const transformStringToBooleanOrViceVersa = (
  value: string | boolean | null,
  isGet: boolean
) => {
  if (value == null) return null

  if (isGet) return booleanToString(value)

  return stringToBoolean(value)
}

export const transformTelephoneNumber = (
  value: string | boolean | null,
  isGet: boolean
) => {
  if (value == null) return null
  if(isGet) return (value as string)?.substring((value as string).indexOf(' ')+1)
  const fixedValue = typeof value === 'string' && value.replace(/\s/g,'').replace('+','')
  return fixedValue.startsWith('0') ? fixedValue.substring(1) : fixedValue
}

export const transformValidationTelephoneCodePlusNumber = (
  value: string | boolean | null,
  isGet: boolean,
  currentData: ApiData | InitialState,
  componentObj: ApiData,
  dropdownOptions: DropdownOptionsContextProps
) => {
  if(isGet) return ''

  const phoneNumberField = phoneValidationMap[`${componentObj.props.name as  keyof typeof phoneValidationMap}`].phoneNumber
  const countryCodeIndexField = phoneValidationMap[`${componentObj.props.name as  keyof typeof phoneValidationMap}`].countryCode
  const countryIndex = dropdownOptions?.countryTelephoneCodes?.findIndex(country => country?.index?.toString() === currentData?.[`${countryCodeIndexField}`]?.value)
  const code = dropdownOptions?.countryTelephoneCodes?.[`${countryIndex}`]?.label?.replace('+','').split?.(' ')?.[0] || ''
  const phoneNumberFixed = transformTelephoneNumber(currentData?.[`${phoneNumberField}`]?.value, false)

  return `${code}${phoneNumberFixed}`
}

export const stringAndBooleanConversion = (
  value: string | boolean,
  isGet: boolean,
  rawType: string
) => {
  if (isGet && rawType === 'boolean') return booleanToString(value)
  if (!isGet && rawType === 'boolean') return stringToBoolean(value)
  return value
}

const convertToDisplayFormat = (value: string) => {
  if (value.match(globalConfig.regex.date)) return value

  return moment(value.substring(0, 10), 'YYYY-MM-DD').format('DD-MM-YYYY')
}

const convertToInternalFormat = (value: string) => {
  if (value.match(globalConfig.regex.dateInternal)) return value

  return moment(value, 'DD-MM-YYYY').format('YYYY-MM-DD')
}

export const convertDate = (value: string, isGet: boolean) => {
  if (isGet) {
    return !value
      ? ''
      : convertToDisplayFormat(value)
  }

  return !value ? '' : convertToInternalFormat(value)
}

// const convertTimezone = (value: string, isGet: boolean, currentData: ApiData | InitialState) => {
//   console.log('timezones', timezones.findIndex(timezone => timezone.label.includes(currentData.cutoffTimeForBusiness.value.split(' ')[1])))
//   if(isGet) return timezones.findIndex(timezone => timezone.label.includes(currentData.cutoffTimeForBusiness.value.split(' ')[1]))
// }

const convertTime = (value: string, isGet: boolean, currentData: ApiData | InitialState) => {
  if(isGet) return value
  if(value === '') return ''
  return `${value} ${timezones[`${currentData.timezonesDropdown.value as  number}`]?.label?.split(' ')?.[0] || 'GMT+0:00'}`
}

const indexToValue = (value: string, isGet: boolean) => {
  if(isGet) return (value === '-1' || value?.toString() === '-1' || value === '0' || value === '') ? '' : (parseInt(value) - 1).toString()
  return  value === '' ? '0' : (parseInt(value) + 1).toString()
}

const transformJsonParse = (value: string, isGet: boolean, dropdownOptions?: DropdownOptionsContextProps) => {
  if(isGet) {
    const parsedValue = JSON.parse(value === '' ? '[]' : value)
    return Array.isArray(parsedValue) &&
      parsedValue?.map(item => (
        {
          index: dropdownOptions?.currencies?.findIndex(optionsItem => optionsItem?.index?.toString() === item).toString(),
          itemIndex: item
        }
      ))
  }
  const itemIndexes = Array.isArray(value) && value?.map(value => value?.itemIndex)

  return JSON.stringify(itemIndexes)
}

const transformRemoveSpaces = (value: string) => {
  return value.replaceAll(' ','')
}

const getValueFromIndex = (value = '', isGet: boolean, name = '') => {
  if(!value) return null
  if(isGet) {
    const itemIndex = listMappings?.[`${name}`]?.findIndex(item => item === value)?.toString()
    return itemIndex !== '-1' ? itemIndex : null
  }
  // @ts-ignore:next-line
  return listMappings?.[`${name}`]?.[`${value}`]
}

export const transformData = (
  componentObj: ComponentDataType,
  currentData: ApiData | InitialState,
  isGet: boolean,
  fetchedData?: ApiData,
  dropdownOptions?: DropdownOptionsContextProps
  // eslint-disable-next-line
): any => {

  const currentDataValue = currentData?.[componentObj?.props?.name]

  const value = isGet
    ? typeof currentDataValue === 'boolean'
      ? currentDataValue
      : currentDataValue === 0
        ? 0
        : currentDataValue || ''
    : typeof currentDataValue?.value === 'string'
      ? currentDataValue?.value.trim()
      : currentDataValue?.value

  if (!componentObj?.transformType) return value

  const rawType = isGet
    ? typeof currentDataValue
    : typeof fetchedData?.[componentObj?.props?.name]

  switch (componentObj?.transformType) {
  case 'stringToNumOrViceVersa':
    return transformStringToNumOrViceVersa(value, isGet)
  case 'stringToBooleanOrViceVersa':
    return transformStringToBooleanOrViceVersa(value, isGet)
  case 'booleanToString':
    return transformStringToBooleanOrViceVersa(value, !isGet)
  case 'stringAndBooleanConversion':
    return stringAndBooleanConversion(value, isGet, rawType)
  case 'date':
    return convertDate(value, isGet)
  case 'time':
    return convertTime(value, isGet, currentData)
  // case 'timezone':
  //   return convertTimezone(value, isGet, currentData)
  case 'indexToValue':
    return indexToValue(value, isGet)
  case 'transformTelephoneNumber':
    return transformTelephoneNumber(value, isGet)
  case 'transformValidationTelephoneCodePlusNumber':
    return transformValidationTelephoneCodePlusNumber(value, isGet, currentData, componentObj, dropdownOptions)
  case 'transformJsonParse':
    return transformJsonParse(value, isGet, dropdownOptions)
  case 'transformRemoveSpaces':
    return transformRemoveSpaces(value)
  case 'getValueFromIndex':
    return getValueFromIndex(value, isGet, componentObj?.props?.name)
  default:
    return value
  }
}

export const setInitialStateData = (
  config: ConfigDataType,
  fetchedData?: ApiData,
  dropdownOptions?: DropdownOptionsContextProps
): InitialState => {
  let initialStateData: InitialState = {}
  const isFileValue = (name: string): boolean => (
    Object.values(globalConsts).includes(name)
  )
  const getEmptyFileStructure  = (): Record<string, null | number | string> => ({
    documentFileExtension: null,
    documentFileType: null,
    documentID: 0,
    documentName: null,
    documentSection: null,
    documentType: null,
  })

  config.content.forEach((contentObj: SectionDataType) => {
    contentObj.content.forEach((obj: ComponentDataType) => {

      if (obj?.componentType === 'grid') {
        initialStateData = {
          ...initialStateData,
          ...setInitialStateData(obj, fetchedData),
        }
      }

      if (obj?.props?.name) {
        initialStateData[obj.props.name] = {
          value:
            fetchedData
              ? typeof fetchedData[obj.props.name] !== 'undefined'
                ? transformData(obj, fetchedData, true, undefined, dropdownOptions)
                : isFileValue(obj.props.name) ? getEmptyFileStructure() : ''
              : isFileValue(obj.props.name) ? getEmptyFileStructure() : '',
          required: obj.props?.required || false,
          show: obj.props?.show === false ? false : true,
        }
        if (obj?.props?.pattern) {
          initialStateData[obj.props.name as string].pattern =
            obj?.props?.pattern
        }
      }

      if (obj?.visualName) {
        initialStateData[obj.visualName] = {
          // value key here used for show/hide
          value: 'true',
          visual: true
        }
      }

      // props needed just in case when there are nested components that hold the same inner components, like radio buttons with same nested inputs
      if (obj?.secondaryName) {
        initialStateData[obj.props.name] = {
          ...initialStateData[obj.props.name],
          secondaryName: ''
        }
      }

    })
  })
  return initialStateData
}

const convertFormDataToPost = (
  formData: InitialState,
  config: ConfigDataType,
  fetchedData: ApiData,
  dropdownOptions?: DropdownOptionsContextProps
): ApiData => {
  let formDataModified: ApiData = {}
  config.content.forEach((contentObj: SectionDataType) => {
    contentObj.content.forEach((obj: ComponentDataType) => {
      if (obj?.componentType === 'grid') {
        formDataModified = {
          ...formDataModified,
          ...convertFormDataToPost(formData, obj, fetchedData, dropdownOptions),
        }
      }
      if (formData[obj?.props?.name]) {

        formDataModified[obj.props.name] =
          formData &&
          typeof formData?.[obj.props.name as string] !== 'undefined'
            ? transformData(obj, formData, false, fetchedData, dropdownOptions)
            : ''
      }
    })
  })
  return formDataModified
}

const deleteTableRow = (tableData: TableObj[], id: number) => {
  return tableData.filter((tableDataObj: TableObj) => tableDataObj.id !== id)
}

const createTableObj = (
  rowDataObj: ApiData,
  tableConfig: TableConfigParam[],
  action: (id: number, action: string, email?: string, accounts?: string[]) => void,
  extraParam?: string
) => {
  const tableObj: TableObj = { id: rowDataObj.userEmailaddress ? (rowDataObj.userId || rowDataObj?.id) : rowDataObj?.id }
  if (extraParam) {
    tableObj[extraParam as string] = rowDataObj?.[extraParam as string]
  }
  tableConfig.forEach((configObj: TableConfigParam, index: number) => {
    if (configObj?.key) {
      tableObj[`col${index + 1}`] = rowDataObj?.[configObj?.key]
    } else {
      tableObj[`col${index + 1}`] = (
        <ActionIconWrapper
          id={rowDataObj.userEmailaddress ? (rowDataObj.userId || rowDataObj?.id) : rowDataObj?.id}
          onClick={() => {
            action(
              rowDataObj.userEmailaddress ?  (rowDataObj.userId || rowDataObj?.id) : rowDataObj?.id,
              configObj?.name === 'pencil' ? 'edit' : 'delete',
              configObj?.name === 'pencil' ? null : rowDataObj?.mail,
              configObj?.name === 'pencil' ? rowDataObj?.accounts : null,
            )
          }}
        >
          <Icon name={configObj?.name} color={configObj?.color} size='s' />
        </ActionIconWrapper>
      )
    }
  })
  return tableObj
}

const transformTableObj = (
  tableObj: TableObj,
  tableConfig: TableConfigParam[],
  extraParam?: string
) => {
  const rowObj: ApiData = { id: tableObj?.id }
  if (extraParam) rowObj[extraParam as string] = tableObj?.[extraParam as string]
  tableConfig.forEach((configObj: TableConfigParam, index: number) => {
    if (configObj?.key) {
      rowObj[configObj?.key as string] = tableObj?.[`col${index + 1}`]
    }
  })

  return rowObj
}

const getEditedTableData = (
  tableData: TableObj[],
  submitData: ApiData,
  tableConfig: TableConfigParam[],
  action: (id: number, action: string, email?: string) => void,
  extraParam?: string
) => {
  const updatedtableData = [...tableData]
  const editedRowData = createTableObj(
    submitData,
    tableConfig,
    action,
    extraParam
  )
  const editedRowIndex = updatedtableData.findIndex(
    (tableObj: TableObj) => tableObj?.id === editedRowData?.id
  )
  updatedtableData[editedRowIndex as number] = editedRowData
  return updatedtableData
}

const addRowToTableData = (
  tableData: TableObj[],
  submitData: ApiData,
  tableConfig: TableConfigParam[],
  action: (id: number, action: string, email?: string) => void,
  extraParam?: string
) => {
  const updatedtableData = [...tableData]
  const addedRowData = createTableObj(
    submitData,
    tableConfig,
    action,
    extraParam
  )
  updatedtableData.push(addedRowData)
  return updatedtableData
}

const getKeysToValidate = (config: ConfigDataType, formData: InitialState) => {
  let keysToValidate: KeysToValidate[] = []
  config.content.forEach((section: SectionDataType) => {
    section.content.forEach((component: ComponentDataType) => {
      if (component?.componentType === 'grid') {
        keysToValidate = [
          ...keysToValidate,
          ...getKeysToValidate(component, formData),
        ]
      }
      if (component?.submitValidation && component?.submitValidation !== '') {
        keysToValidate.push({
          key: component.props.name,
          value: formData?.[component.props.name as string].value,
          submitValidation: component.submitValidation,
        })
      }
    })
  })
  return keysToValidate
}

export const isInvalidValue = (value: string, regex: RegExp) => (
  value.trim() !== '' && regex && !value.trim().match(regex)
)

const checkIfIsInvalid = (validateList: boolean, value: string, regex: RegExp) => {
  if (validateList) {
    const splittedList = value.split(',')
    return splittedList.length > 0 && splittedList.some(element => isInvalidValue(element, regex))
  }
  return isInvalidValue(value, regex)
}

const runSubmitValidation = (keysToValidate: KeysToValidate[]) => {
  const errorKeys: string[] = []

  keysToValidate.forEach(keyObj => {
    if (keyObj && keyObj.submitValidation) {
      // if submitValidation starts with '*' validate a comma-separated list of regex i.e. '*email', '*date'...
      const validateList = keyObj.submitValidation.startsWith('*')
      const regexType = validateList
        ? keyObj.submitValidation.slice(1)
        : keyObj.submitValidation

      // Make sure that the submitValidation key is on the globalConfig?.regex list
      const regex: RegExp = globalConfig?.regex?.[regexType as keyof RegexType]
      if (!regex) console.error(`The submitValidation key ("${regexType}") should have a regex with the same name on the globalConfig?.regex`)

      if (checkIfIsInvalid(validateList, keyObj.value, regex)) {
        errorKeys.push(keyObj.key)
      }
    }
  })

  return errorKeys
}

const getErrorMessage = (errorObject: Record<string, string>): string => {
  return t(`onboarding.errors.${errorObject.errorcode}`) || errorObject.errordescription?.split(':')[1] || t('error.invalidValue')
}

export const injectErrorPropToConfig = (
  config: ConfigDataType,
  errorKeys: string[],
  errorsFromServer?: Record<string, string>[]
): ConfigDataType => {
  const output = { ...config }
  config.content.forEach((section: SectionDataType, sectionIndex: number) =>
    section.content.forEach(
      (component: ComponentDataType, componentIndex: number) => {
        if (component?.content) {
          component.content.forEach(
            (subConfig: ComponentDataType, subConfigIndex: number) => {
              if (subConfig?.content) {
                subConfig.content.forEach(
                  (
                    subComponent: ComponentDataType,
                    subComponentIndex: number
                  ) => {
                    if (subComponent.props && (!errorsFromServer || errorsFromServer.length === 0)){
                      output.content[`${sectionIndex}`]
                        .content[`${componentIndex}`]
                        .content[`${subConfigIndex}`]
                        .content[`${subComponentIndex}`]
                        .props.error = errorKeys.includes(subComponent.props.name)
                          ? t('error.invalidValue')
                          : ''
                    }
                    const errorObject = errorsFromServer?.find(error => error.key === subComponent.props?.name)
                    if(errorObject) {
                      output.content[`${sectionIndex}`]
                        .content[`${componentIndex}`]
                        .content[`${subConfigIndex}`]
                        .content[`${subComponentIndex}`]
                        .props.error = getErrorMessage(errorObject)
                    }
                  }
                )
              }
            }
          )
        } else {
          if (component.props && (!errorsFromServer || errorsFromServer.length === 0)){
            output.content[`${sectionIndex}`]
              .content[`${componentIndex}`]
              .props.error = errorKeys.includes(component.props.name)
                ? t('error.invalidValue')
                : ''
          }
          const errorObject = errorsFromServer?.find(error => error.key === component.props?.name)
          if(errorObject) {
            output.content[`${sectionIndex}`]
              .content[`${componentIndex}`]
              .props.error = getErrorMessage(errorObject)
          }
        }
      }
    )
  )
  return output
}

const containSomeWithIncludes = (objToCheck: InitialStateValue, valuesToFind: string[]) => {
  return  Object.keys(objToCheck).some(subItem => valuesToFind.includes(subItem))
}

const cleanFormData = (formData: InitialState, { keysToRemove }: ConfigDataType) => {
  if(!formData) { return }

  let formDataNoVisuals = {}

  Object.entries(formData).forEach(([key, formItemValue]) => {

    const itemValue = formItemValue.value

    if (containSomeWithIncludes(formItemValue, keysToRemove)) { return false }

    if (Array.isArray(itemValue) && (itemValue.length === 0 || (Object.keys(itemValue[0]).length > 0 && containSomeWithIncludes(itemValue[0], keysToRemove)))){ return false }

    if (itemValue.constructor === Object
        // @ts-ignore
        && (Object.keys(itemValue).length === 0 || containSomeWithIncludes(itemValue, keysToRemove))) { return false }

    formDataNoVisuals = {
      ...formDataNoVisuals,
      [`${key}`]: formItemValue
    }

  })

  return formDataNoVisuals
}

const convertContentType = (postHeaders: RequestConfig['headers'], convertedData: ApiData) => {
  if (postHeaders.contentType === 'multipart/form-data'){
    const formDataToSend = new FormData()
    for (const key in convertedData) {
      formDataToSend.append(key, convertedData[`${key}`])
    }
    return formDataToSend
  }

}

const isChecked = (formData: InitialState, props: { name: string }) => {
  const checkedOrNot = typeof formData?.[props?.name]?.value === 'boolean'
    ? formData?.[props?.name]?.value
    : formData?.[props?.name]?.value === 'true'
  return checkedOrNot
}

export default {
  componentsBuilder,
  setInitialStateData,
  sectionBuilder,
  injectTranslation,
  transformData,
  convertFormDataToPost,
  deleteTableRow,
  createTableObj,
  transformTableObj,
  getEditedTableData,
  addRowToTableData,
  getKeysToValidate,
  runSubmitValidation,
  injectErrorPropToConfig,
  convertDate,
  cleanFormData,
  convertContentType,
}
